import { useQuery } from '@tanstack/react-query';

import { useAuth } from '@/contexts/AuthContext';
import { createApiClient } from '@/shared/api/apiClient';
import type { components } from '@/shared/api/openapi';

import type { BaseQuery } from './baseQuery.types';
import { getImportPollingInterval } from './importPolling';

export type ImportedBudgetGroupFileHistoryItem =
  components['schemas']['GetImportedBudgetGroupsFileHistoryEndpoint_ImportedFileHistoryResponse'];

type GetImportedBudgetGroupsFileHistoryPagedResponse =
  components['schemas']['GetImportedBudgetGroupsFileHistoryEndpoint_ImportedFileHistoryPagedResponse'];

export interface UseImportedBudgetFileHistoryReturn extends BaseQuery {
  importHistory: GetImportedBudgetGroupsFileHistoryPagedResponse | null;
}

export function useImportedBudgetFileHistory(
  organizationId: number | undefined,
  trackedImportId?: number | null,
  page: number = 1,
  pageSize: number = 10,
  sortedBy: string | null = null,
): UseImportedBudgetFileHistoryReturn {
  const { getAccessToken } = useAuth();

  const getImportedBudgetFileHistory = async () => {
    if (!organizationId || organizationId === 0) {
      return null;
    }

    const client = createApiClient(getAccessToken);
    const { data } = await client.GET('/api/organizations/{organizationId}/budget-groups/imported-file-history', {
      params: {
        path: { organizationId },
        query: {
          Page: page,
          PageSize: pageSize,
          ...(sortedBy ? { SortedBy: sortedBy } : {}),
        },
      },
    });

    if (!data) {
      throw new Error('No data returned from API');
    }

    return data as GetImportedBudgetGroupsFileHistoryPagedResponse;
  };

  const query = useQuery({
    queryKey: ['importedBudgetFileHistory', organizationId, page, pageSize, sortedBy],
    queryFn: async () => {
      try {
        return await getImportedBudgetFileHistory();
      } catch (err) {
        if (err instanceof Error) {
          throw err;
        }
        throw new Error(err ? String(err) : 'Unknown error occurred');
      }
    },
    enabled: !!organizationId && organizationId > 0,
    retry: false,
    refetchInterval: (query) => getImportPollingInterval(query.state.data?.items ?? undefined, trackedImportId),
  });

  const { data, isLoading, error, refetch, isRefetching } = query;

  return {
    importHistory: data ?? null,
    isLoading,
    isRefetching,
    error: error as Error | null,
    refetch,
  };
}
